"use client"

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { format, addDays } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import ReminderService from '@/api/reminder.service';
import CustomerService from '@/api/customers.service';
import DealService from '@/api/deal.service';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  type: z.string().min(1, 'Reminder type is required'),
  message: z.string().min(3, 'Message is required'),
  dueDate: z.date({
    required_error: "Due date is required",
  }),
  relatedTo: z.string().optional(),
  relatedItemId: z.string().optional(),
});

interface CreateReminderFormProps {
  businessId: number;
  onSuccess: () => void;
}

export default function CreateReminderForm({ businessId, onSuccess }: CreateReminderFormProps) {
  const [customers, setCustomers] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingRelated, setIsFetchingRelated] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: '',
      message: '',
      dueDate: addDays(new Date(), 1), // Default to tomorrow
      relatedTo: '',
      relatedItemId: '',
    },
  });
  
  const relatedToValue = form.watch('relatedTo');
  
  useEffect(() => {
    const fetchRelatedItems = async () => {
      if (!relatedToValue) return;
      
      setIsFetchingRelated(true);
      try {
        if (relatedToValue === 'customer') {
          const response = await CustomerService.getAllCustomers(businessId);
          if (response.data.result === 'success') {
            setCustomers(response.data.customers || []);
          }
        } else if (relatedToValue === 'deal') {
          const response = await DealService.getAllDeals(businessId);
          if (response.data.result === 'success') {
            setDeals(response.data.deals || []);
          }
        }
      } catch (error) {
        console.error("Error fetching related items:", error);
        toast({
          title: "Error",
          description: "Failed to load related items.",
          variant: "destructive",
        });
      } finally {
        setIsFetchingRelated(false);
      }
    };
    
    fetchRelatedItems();
  }, [relatedToValue, businessId, toast]);
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const reminderData: any = {
        type: values.type,
        message: values.message,
        dueDate: values.dueDate,
      };
      
      if (values.relatedTo === 'customer' && values.relatedItemId) {
        reminderData.customerId = parseInt(values.relatedItemId);
      } else if (values.relatedTo === 'deal' && values.relatedItemId) {
        reminderData.dealId = parseInt(values.relatedItemId);
      }
      
      const response = await ReminderService.createReminder(businessId, reminderData);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Reminder created successfully!",
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to create reminder.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating reminder:", error);
      toast({
        title: "Error",
        description: "An error occurred while creating the reminder.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const generateSuggestion = (type: string) => {
    let suggestion = '';
    
    switch (type) {
      case 'birthday':
        suggestion = 'Birthday celebration';
        break;
      case 'follow-up':
        suggestion = 'Follow up on last conversation';
        break;
      case 'meeting':
        suggestion = 'Schedule meeting to discuss next steps';
        break;
      case 'task':
        suggestion = 'Complete assigned task';
        break;
      default:
        suggestion = '';
    }
    
    form.setValue('message', suggestion);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reminder Type</FormLabel>
              <Select 
                onValueChange={(value) => {
                  field.onChange(value);
                  generateSuggestion(value);
                }}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reminder type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="birthday">Birthday</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reminder Message</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe the reminder..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="dueDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Due Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Select date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="relatedTo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Related To (Optional)</FormLabel>
              <Select 
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Not related to anything" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="na">Not related to anything</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="deal">Deal</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {relatedToValue && (
          <FormField
            control={form.control}
            name="relatedItemId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {relatedToValue === 'customer' ? 'Customer' : 'Deal'}
                </FormLabel>
                <Select 
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isFetchingRelated}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={`Select a ${relatedToValue}`} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {isFetchingRelated ? (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading...
                      </div>
                    ) : relatedToValue === 'customer' ? (
                      customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id.toString()}>
                          {customer.firstName} {customer.lastName}
                          {customer.companyName && ` (${customer.companyName})`}
                        </SelectItem>
                      ))
                    ) : (
                      deals.map((deal) => (
                        <SelectItem key={deal.id} value={deal.id.toString()}>
                          {deal.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Reminder
          </Button>
        </div>
      </form>
    </Form>
  );
} 